import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AuthShell from '@/pages/auth/components/AuthShell';
import Stepper from '@/pages/auth/register/components/Stepper';
import StepBasics from '@/pages/auth/register/components/StepBasics';
import StepLocation from '@/pages/auth/register/components/StepLocation';
import StepDocuments from '@/pages/auth/register/components/StepDocuments';
import StepReview from '@/pages/auth/register/components/StepReview';
import Button from '@/components/base/Button';
import Toast from '@/components/base/Toast';
import {
  initialRegisterData,
  type RegisterData,
} from '@/pages/auth/register/types';

type Errors = Partial<Record<keyof RegisterData, string>>;

export default function RegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<RegisterData>(initialRegisterData);
  const [errors, setErrors] = useState<Errors>({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const update = (key: keyof RegisterData, value: string) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const toggleCoverage = (district: string) => {
    setData((prev) => ({
      ...prev,
      coverage: prev.coverage.includes(district)
        ? prev.coverage.filter((item) => item !== district)
        : [...prev.coverage, district],
    }));
    setErrors((prev) => ({ ...prev, coverage: undefined }));
  };

  const validateStep = (target: number) => {
    const next: Errors = {};
    if (target === 1) {
      if (!data.name.trim()) next.name = 'اسم المغسلة مطلوب';
      if (!data.phone.trim()) next.phone = 'رقم الهاتف مطلوب';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()))
        next.email = 'يرجى إدخال بريد إلكتروني صحيح';
      if (data.password.length < 6) next.password = 'كلمة المرور يجب أن تكون 6 أحرف على الأقل';
      if (data.password !== data.confirmPassword)
        next.confirmPassword = 'كلمتا المرور غير متطابقتين';
    }
    if (target === 2) {
      if (!data.district.trim()) next.district = 'الحي مطلوب';
      if (!data.street.trim()) next.street = 'الشارع مطلوب';
      if (!data.buildingNo.trim()) next.buildingNo = 'رقم المبنى مطلوب';
      if (data.coverage.length === 0) next.coverage = 'اختر حياً واحداً على الأقل';
    }
    if (target === 3) {
      if (!data.commercialRegName) next.commercialRegName = 'السجل التجاري مطلوب';
      if (!data.idDocName) next.idDocName = 'الهوية مطلوبة';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const goNext = () => {
    if (!validateStep(step)) {
      setToast('يرجى إكمال الحقول المطلوبة قبل المتابعة');
      return;
    }
    setStep((prev) => Math.min(prev + 1, 4));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = () => {
    if (!validateStep(1) || !validateStep(2) || !validateStep(3)) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      navigate('/auth/under-review');
    }, 1000);
  };

  return (
    <AuthShell brandHeadline="انضم لأكبر شبكة غسيل وكوي في مدينتك">
      {toast && <Toast type="error" message={toast} onClose={() => setToast(null)} />}

      <div className="mb-1">
        <Link
          to="/auth/login"
          className="inline-flex items-center gap-1 text-[13px] font-medium text-secondary-600 transition-colors hover:text-secondary-700"
        >
          <i className="ri-arrow-right-line text-base" aria-hidden="true"></i>
          العودة لتسجيل الدخول
        </Link>
      </div>

      <h1 className="mt-4 font-bold text-2xl text-foreground-950">تسجيل مغسلة جديدة</h1>
      <p className="mt-2 text-sm text-foreground-500">
        أكمل البيانات التالية لأجل انضمام مغسلتك إلى المنصة
      </p>

      <div className="mt-6">
        <Stepper current={step} />
      </div>

      <div className="min-h-[280px]">
        {step === 1 && <StepBasics data={data} errors={errors} update={update} />}
        {step === 2 && (
          <StepLocation
            data={data}
            errors={errors}
            update={update}
            toggleCoverage={toggleCoverage}
          />
        )}
        {step === 3 && <StepDocuments data={data} update={update} />}
        {step === 4 && <StepReview data={data} onEdit={setStep} />}
      </div>

      <div className="mt-8 flex items-center gap-3">
        {step > 1 && (
          <Button type="button" variant="ghost" size="lg" onClick={goBack} icon="ri-arrow-right-line">
            السابق
          </Button>
        )}
        {step < 4 ? (
          <Button
            type="button"
            variant="cta"
            size="lg"
            fullWidth={step === 1}
            className={step > 1 ? 'flex-1' : ''}
            icon="ri-arrow-left-line"
            iconPosition="end"
            onClick={goNext}
          >
            التالي
          </Button>
        ) : (
          <Button
            type="button"
            variant="cta"
            size="lg"
            fullWidth
            loading={loading}
            icon="ri-send-plane-line"
            onClick={handleSubmit}
          >
            إرسال الطلب
          </Button>
        )}
      </div>
    </AuthShell>
  );
}